import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import supabase from '../utils/supabaseClient';
import './Profile.css';

const Profile = () => {
    const { isDarkTheme } = useTheme();
    const navigate = useNavigate();
    const { user, profile, loading, profileLoading, updateProfile, refreshProfile } = useAuth();
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });

    const [displayName, setDisplayName] = useState(profile?.display_name || '');
    const [bio, setBio] = useState(profile?.bio || '');
    const [interests, setInterests] = useState(Array.isArray(profile?.interests) ? profile.interests : []);
    const [newInterest, setNewInterest] = useState('');

    const [photoURL, setPhotoURL] = useState(profile?.avatar_url || '');
    const [photoFile, setPhotoFile] = useState(null);
    const fileInputRef = useRef(null);

    const [activeTab, setActiveTab] = useState('general');

    useEffect(() => {
        if (!profile) return;
        setDisplayName(profile.display_name || '');
        setBio(profile.bio || '');
        setInterests(Array.isArray(profile.interests) ? profile.interests : []);
        setPhotoURL(profile.avatar_url || '');
    }, [profile]);

    const handlePhotoClick = () => {
        fileInputRef.current?.click();
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setPhotoFile(file);

        const reader = new FileReader();
        reader.onloadend = () => {
            setPhotoURL(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const handleAddInterest = () => {
        const trimmed = newInterest.trim();
        if (trimmed !== '' && !interests.includes(trimmed)) {
            setInterests([...interests, trimmed]);
            setNewInterest('');
        }
    };

    const handleRemoveInterest = (interest) => {
        setInterests(interests.filter((item) => item !== interest));
    };

    const uploadAvatar = async (file) => {
        if (!user) throw new Error('No authenticated user available for avatar upload.');
        const fileExt = file.name.split('.').pop();
        const fileName = `${user.id}/${Date.now()}.${fileExt}`;

        const { data: uploadData, error: uploadError } = await supabase.storage
            .from('avatars')
            .upload(fileName, file, {
                cacheControl: '3600',
                upsert: true,
                contentType: file.type,
            });

        if (uploadError) {
            console.error('Avatar upload failed', uploadError);
            throw new Error(uploadError.message || 'Avatar upload failed');
        }

        const { data: publicData, error: publicError } = supabase.storage
            .from('avatars')
            .getPublicUrl(uploadData?.path || fileName);

        if (publicError) {
            console.error('Failed to resolve avatar public URL', publicError);
            throw new Error(publicError.message || 'Failed to resolve avatar URL');
        }

        return publicData.publicUrl;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage({ text: '', type: '' });

        try {
            const updates = {
                display_name: displayName,
                bio,
                interests,
            };

            if (photoFile) {
                const uploadedUrl = await uploadAvatar(photoFile);
                updates.avatar_url = uploadedUrl;
                setPhotoURL(uploadedUrl);
                setPhotoFile(null);
            }

            await updateProfile(updates);
            await refreshProfile();

            setMessage({
                text: 'Profile updated successfully!',
                type: 'success',
            });
        } catch (error) {
            console.error('Error updating profile:', error);
            setMessage({
                text: error.message || 'Failed to update profile. Please try again.',
                type: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    if (!user && !loading) {
        return (
            <div className="profile-container">
                <div className="profile-header">
                    <h1>Session Expired</h1>
                    <p>Please sign back in to view your profile.</p>
                </div>
            </div>
        );
    }

    const primaryEmail = user?.email || '';
    const fallbackInitial = (displayName || primaryEmail || 'U').charAt(0).toUpperCase();

    return (
        <div className={`profile-container ${isDarkTheme ? 'dark' : 'light'}`}>
            <div className="profile-header">
                <h1>Your Profile</h1>
                <p>Manage your account settings and preferences</p>
            </div>

            {message.text && (
                <div className={`message ${message.type}`}>
                    {message.text}
                </div>
            )}

            <div className="profile-content">
                <div className="profile-sidebar">
                    <div className="profile-photo-container" onClick={handlePhotoClick}>
                        {photoURL ? (
                            <img src={photoURL} alt="Profile" className="profile-photo" />
                        ) : (
                            <div className="profile-photo-placeholder">
                                {fallbackInitial}
                            </div>
                        )}
                        <div className="photo-edit-overlay">Change Photo</div>
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handlePhotoChange}
                            style={{ display: 'none' }}
                            accept="image/*"
                        />
                    </div>

                    <div className="profile-tabs">
                        <button
                            className={`tab-button ${activeTab === 'general' ? 'active' : ''}`}
                            onClick={() => setActiveTab('general')}
                        >
                            General
                        </button>
                        <button
                            className={`tab-button ${activeTab === 'preferences' ? 'active' : ''}`}
                            onClick={() => setActiveTab('preferences')}
                        >
                            Preferences
                        </button>
                    </div>
                </div>

                <div className="profile-main">
                    <form onSubmit={handleSubmit} className="profile-form">
                        {activeTab === 'general' && (
                            <div className="form-section">
                                <h2>General Information</h2>

                                <div className="form-group">
                                    <label htmlFor="displayName">Display Name</label>
                                    <input
                                        id="displayName"
                                        type="text"
                                        value={displayName}
                                        onChange={(e) => setDisplayName(e.target.value)}
                                        placeholder="Your display name"
                                        disabled={profileLoading}
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="email">Email Address</label>
                                    <div className="email-info">
                                        <span>{primaryEmail}</span>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="bio">Bio</label>
                                    <textarea
                                        id="bio"
                                        value={bio}
                                        onChange={(e) => setBio(e.target.value)}
                                        placeholder="Tell us about yourself"
                                        rows={4}
                                        disabled={profileLoading}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Interests</label>
                                    <div className="interests-container">
                                        {interests.map((interest, index) => (
                                            <div key={index} className="interest-tag">
                                                {interest}
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveInterest(interest)}
                                                    className="remove-interest"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="add-interest">
                                        <input
                                            type="text"
                                            value={newInterest}
                                            onChange={(e) => setNewInterest(e.target.value)}
                                            placeholder="Add an interest"
                                            disabled={profileLoading}
                                        />
                                        <button
                                            type="button"
                                            onClick={handleAddInterest}
                                            className="add-interest-btn"
                                            disabled={profileLoading}
                                        >
                                            Add
                                        </button>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Account Management</label>
                                    <p className="field-note">
                                        Password resets and multi-factor authentication can be managed from the Supabase dashboard.
                                    </p>
                                </div>
                            </div>
                        )}

                        {activeTab === 'preferences' && (
                            <div className="form-section">
                                <h2>Preferences</h2>

                                <div className="form-group">
                                    <label>Theme Preference</label>
                                    <p className="field-note">
                                        You can change your theme using the theme toggle button in the corner of the screen.
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="form-actions">
                            <button
                                type="submit"
                                className="save-button"
                                disabled={saving || profileLoading}
                            >
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                            <button
                                type="button"
                                className="cancel-button"
                                onClick={() => navigate('/dashboard')}
                                disabled={saving}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
                {profileLoading && (
                    <div className="profile-loading-banner">Loading latest profile details...</div>
                )}
            </div>
        </div>
    );
};

export default Profile;